const CHUNK_ERROR_IDENTITY_PATTERN =
  /(?:ChunkLoadError|CSS_CHUNK_LOAD_FAILED|CHUNK_LOAD_FAILED)/i;
const CHUNK_LOAD_MESSAGE_PATTERN =
  /(?:Failed to load chunk|Loading (?:CSS )?chunk .* failed)/i;
const NATIVE_DYNAMIC_IMPORT_ERROR_PATTERN =
  /(?:Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module)/i;
const STATIC_ASSET_PATTERN =
  /\/_next\/static\/(?:chunks|css)\/[A-Za-z0-9_./-]+\.(?:js|css)(?:\?[A-Za-z0-9_.~!$&'()*+,;=:@%/-]+)?/;
const RECOVERY_STORAGE_PREFIX = "lootstack:chunk-reload:";
const DEPLOYMENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,160}$/;

type ErrorWithChunkMetadata = Error & {
  code?: unknown;
  request?: unknown;
};

export type ChunkLoadTelemetry = Record<string, unknown> & {
  chunk_error: boolean;
  chunk_error_code?: string;
  chunk_error_name?: string;
  chunk_failed_asset?: string;
  chunk_failed_asset_deployment_id?: string;
  chunk_loaded_asset_deployment_ids?: string[];
  chunk_loaded_next_asset_count?: number;
  chunk_deployment_mismatch?: boolean;
  document_age_ms?: number;
  document_deployment_id?: string;
  document_pathname?: string;
};

export type ChunkLoadTelemetryEnvironment = {
  documentDeploymentId?: string | null;
  documentPathname?: string | null;
  documentAgeMs?: number | null;
  origin?: string;
  resourceNames?: string[];
};

export type ChunkLoadRecoveryDecision = {
  recoveryKey?: string;
  shouldReload: boolean;
  status:
    | "already-attempted"
    | "not-a-chunk-error"
    | "scheduled"
    | "storage-unavailable";
};

function errorText(error: unknown): string {
  if (error instanceof Error) {
    const chunkError = error as ErrorWithChunkMetadata;
    return [error.name, error.message, chunkError.code, chunkError.request]
      .filter((value) => typeof value === "string")
      .join(" ");
  }
  return String(error ?? "");
}

export function isChunkLoadError(error: unknown): boolean {
  const text = errorText(error);
  if (
    CHUNK_ERROR_IDENTITY_PATTERN.test(text) ||
    NATIVE_DYNAMIC_IMPORT_ERROR_PATTERN.test(text)
  ) {
    return true;
  }

  // Framework wording alone is too generic (application errors can mention
  // "load chunk metadata"). Require the failed Next asset unless the error's
  // name/code already identified it above.
  return (
    CHUNK_LOAD_MESSAGE_PATTERN.test(text) && STATIC_ASSET_PATTERN.test(text)
  );
}

export function extractFailedStaticAsset(error: unknown): string | null {
  const match = errorText(error)
    .replaceAll("&amp;", "&")
    .match(STATIC_ASSET_PATTERN);
  if (!match) {
    return null;
  }

  try {
    const url = new URL(match[0], "https://asset.invalid");
    const deploymentId = url.searchParams.get("dpl") || "";
    url.search = "";
    if (DEPLOYMENT_ID_PATTERN.test(deploymentId)) {
      url.searchParams.set("dpl", deploymentId);
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

function deploymentIdFromAsset(asset: string | null): string {
  if (!asset) {
    return "";
  }
  return new URL(asset, "https://asset.invalid").searchParams.get("dpl") || "";
}

function browserTelemetryEnvironment(): ChunkLoadTelemetryEnvironment {
  if (typeof window === "undefined") {
    return {};
  }

  let resourceNames: string[] = [];
  try {
    resourceNames = performance
      .getEntriesByType("resource")
      .map((entry) => entry.name)
      .filter((name) => name.includes("/_next/static/"));
  } catch {
    // Performance entries are optional diagnostics, never a reason to fail.
  }

  const registeredDeploymentId = (
    globalThis as typeof globalThis & { NEXT_DEPLOYMENT_ID?: unknown }
  ).NEXT_DEPLOYMENT_ID;

  return {
    documentAgeMs:
      typeof performance?.now === "function"
        ? Math.round(performance.now())
        : null,
    // Next removes data-dpl-id during bootstrap after copying it to this
    // global. The DOM value remains useful only for very early failures.
    documentDeploymentId:
      (typeof registeredDeploymentId === "string" && registeredDeploymentId) ||
      document.documentElement?.dataset?.dplId ||
      null,
    documentPathname: window.location.pathname,
    origin: window.location.origin,
    resourceNames,
  };
}

export function getChunkLoadTelemetry(
  error: unknown,
  environment: ChunkLoadTelemetryEnvironment = {}
): ChunkLoadTelemetry {
  const resolvedEnvironment = {
    ...browserTelemetryEnvironment(),
    ...environment,
  };
  const chunkError = error as Partial<ErrorWithChunkMetadata> | null;
  const failedAsset = extractFailedStaticAsset(error);
  const failedAssetDeploymentId = deploymentIdFromAsset(failedAsset);
  const documentDeploymentId = resolvedEnvironment.documentDeploymentId || "";
  const loadedNextAssets = (resolvedEnvironment.resourceNames || []).flatMap(
    (name) => {
      try {
        const url = new URL(
          name,
          resolvedEnvironment.origin || "https://asset.invalid"
        );
        return url.pathname.startsWith("/_next/static/") ? [url] : [];
      } catch {
        return [];
      }
    }
  );
  const loadedAssetDeploymentIds = [
    ...new Set(
      loadedNextAssets
        .map((url) => url.searchParams.get("dpl") || "")
        .filter(Boolean)
    ),
  ].slice(0, 10);

  return {
    chunk_error: isChunkLoadError(error),
    ...(typeof chunkError?.name === "string" && chunkError.name
      ? { chunk_error_name: chunkError.name }
      : {}),
    ...(typeof chunkError?.code === "string" && chunkError.code
      ? { chunk_error_code: chunkError.code }
      : {}),
    ...(failedAsset ? { chunk_failed_asset: failedAsset } : {}),
    ...(failedAssetDeploymentId
      ? { chunk_failed_asset_deployment_id: failedAssetDeploymentId }
      : {}),
    ...(documentDeploymentId
      ? { document_deployment_id: documentDeploymentId }
      : {}),
    ...(resolvedEnvironment.documentPathname
      ? { document_pathname: resolvedEnvironment.documentPathname }
      : {}),
    ...(loadedAssetDeploymentIds.length > 0
      ? { chunk_loaded_asset_deployment_ids: loadedAssetDeploymentIds }
      : {}),
    ...(resolvedEnvironment.resourceNames
      ? {
          chunk_loaded_next_asset_count: loadedNextAssets.length,
        }
      : {}),
    ...(typeof resolvedEnvironment.documentAgeMs === "number"
      ? {
          document_age_ms: Math.max(
            0,
            Math.round(resolvedEnvironment.documentAgeMs)
          ),
        }
      : {}),
    ...(documentDeploymentId && failedAssetDeploymentId
      ? {
          chunk_deployment_mismatch:
            documentDeploymentId !== failedAssetDeploymentId,
        }
      : {}),
  };
}

function recoveryBuildId(telemetry: ChunkLoadTelemetry): string {
  const value =
    telemetry.document_deployment_id ||
    telemetry.chunk_failed_asset_deployment_id ||
    "unknown";
  return (
    String(value)
      .replace(/[^A-Za-z0-9_-]/g, "_")
      .slice(0, 128) || "unknown"
  );
}

function recoveryPathname(telemetry: ChunkLoadTelemetry): string {
  const pathname = String(telemetry.document_pathname || "unknown-path");
  return encodeURIComponent(pathname).slice(0, 256) || "unknown-path";
}

export function claimChunkLoadRecovery(
  telemetry: ChunkLoadTelemetry,
  options: {
    storage?: Pick<Storage, "getItem" | "setItem"> | null;
    now?: () => number;
  } = {}
): ChunkLoadRecoveryDecision {
  if (!telemetry.chunk_error) {
    return { shouldReload: false, status: "not-a-chunk-error" };
  }

  const storage =
    options.storage === undefined ? browserSessionStorage() : options.storage;
  const now = options.now || (() => Date.now());
  // Deployment IDs are unique per Cloud Build. Pairing that build with the
  // sanitized route permits one recovery on each affected route without loops.
  const recoveryKey = `${RECOVERY_STORAGE_PREFIX}${recoveryBuildId(telemetry)}:${recoveryPathname(telemetry)}`;
  if (!storage) {
    return { recoveryKey, shouldReload: false, status: "storage-unavailable" };
  }

  try {
    if (storage.getItem(recoveryKey)) {
      return { recoveryKey, shouldReload: false, status: "already-attempted" };
    }
    storage.setItem(
      recoveryKey,
      JSON.stringify({
        asset: telemetry.chunk_failed_asset || null,
        attemptedAt: now(),
      })
    );
    return { recoveryKey, shouldReload: true, status: "scheduled" };
  } catch {
    // Without a durable tab-scoped marker, an automatic reload could loop.
    return { recoveryKey, shouldReload: false, status: "storage-unavailable" };
  }
}

function browserSessionStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
